/**
 * 별채대화 입력창 (시안 03 README 5.12 · 09 통합본 · 12 대화 칩 · @ 멘션).
 *
 *   (대화 칩 줄 / 멘션 목록) → [멘션 꼬리표 · 입력칸 "별채에 한마디"] [전송]
 *
 * - 왼쪽 '+' 메뉴는 없앴습니다(시안 09 · 05 결정 27 · 08 결정 36). 고민은 상회칠판 고민 칸 「고민 올리기」로만 올립니다
 * - Enter 는 줄바꿈, 보내기는 전송 버튼을 눌렀을 때만(사용자 요청 2026-10-15). 멘션 목록이 열려 있을 때의 Enter 는 멤버 고르기
 * - 입력 중 신호(C-28): 쓰기 시작하면 onCompose('start'), 쓰는 동안 5초마다 한 번 더(서버는 8초 만료),
 *   비우면 onCompose('end'). 키 입력 내용은 보내지 않습니다. 보내기는 서버가 끝으로 칩니다
 * - 대화 칩(C-29 · D74, 결정 50 · 51): 입력칸이 비어 있을 때만 입력창 위에, 누르면 바로 보냄. 쓰기 시작하면 접고 비우면 다시
 * - @ 멘션(결정 52 · 53): 「@」 → 멤버 목록(지금 물은 사람 표시, 이름 앞부분으로 좁힘) → 꼬리표(2명까지, 지우기 한 번에 삭제).
 *   꼬리표만 있고 입력칸이 비면 칩 줄이 다시 보이고, 칩을 누르면 「@멤버 + 칩」 한 말로 바로 보냄
 * - 꼬리표와 입력칸은 한 입력 상자(InputBox, DEV-18b): 포커스 테두리를 함께. 꼬리표를 누르면 그 자리 멤버를 다시 고름
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowUp } from 'lucide-react';
import { MEMBER_KEYS, getMember } from '@config/members/index.js';
import { Avatar } from '../../components/Avatar.jsx';
import { ChipRow } from '../../components/ChipRow.jsx';
import { InputBox } from '../../components/InputBox.jsx';
import { track } from '../../api/track.js';

const MAX_LINES = 5;
const LINE_HEIGHT_PX = 26;
const VERTICAL_PADDING_PX = 16;
const MAX_LENGTH = 1000;
const COMPOSE_REFRESH_MS = 5000;
const MAX_MENTIONS = 2;
const MENTION_QUERY = /(^|\s)@([^\s@]{0,6})$/;

function nameOf(memberKey) {
    return getMember(memberKey)?.name ?? '';
}

/** 「@손 팀장 @이모 」 머리 */
export function mentionPrefix(mentions) {
    return mentions.map((memberKey) => `@${nameOf(memberKey)}`).join(' ');
}

function matchMembers(query) {
    const compact = query.replace(/\s+/g, '');
    return MEMBER_KEYS.filter((memberKey) => nameOf(memberKey).replace(/\s+/g, '').startsWith(compact));
}

export function RoomComposer({ onSend, onCompose = null, disabled = false, chips = null, onChipTap = null }) {
    const [value, setValue] = useState('');
    const [mentions, setMentions] = useState([]);
    const [picker, setPicker] = useState(null);
    const textareaRef = useRef(null);
    // 마지막으로 「입력 중」을 알린 때(ms). 0 이면 지금 입력 중이 아님
    const composeSentAt = useRef(0);
    const shownSetRef = useRef(null);
    const canSend = !disabled && value.trim().length > 0;
    const showChips = !disabled && Boolean(chips?.items?.length) && !value.trim() && !picker && Boolean(onChipTap);

    // 칩 묶음이 처음 보일 때 한 번(23.8)
    useEffect(() => {
        if (showChips && shownSetRef.current !== chips.chipSetsId) {
            shownSetRef.current = chips.chipSetsId;
            track('chip_shown', { count: chips.items.length, act: chips.speechAct ?? 'none' });
        }
    }, [showChips, chips]);

    useLayoutEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) {
            return;
        }
        textarea.style.height = 'auto';
        const borderHeight = textarea.offsetHeight - textarea.clientHeight;
        const contentHeight = textarea.scrollHeight + borderHeight;
        const maxHeight = LINE_HEIGHT_PX * MAX_LINES + VERTICAL_PADDING_PX + borderHeight;
        textarea.style.height = `${Math.min(contentHeight, maxHeight)}px`;
        textarea.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
    }, [value]);

    function signalCompose(next) {
        if (!onCompose) {
            return;
        }
        const now = Date.now();
        if (next.trim()) {
            if (now - composeSentAt.current >= COMPOSE_REFRESH_MS) {
                composeSentAt.current = now;
                onCompose('start');
            }
        } else if (composeSentAt.current) {
            composeSentAt.current = 0;
            onCompose('end');
        }
    }

    function handleChange(event) {
        const next = event.target.value;
        if (!value.trim() && next.trim() && chips?.items?.length) {
            track('chip_dismissed', { reason: 'input' });
        }
        setValue(next);
        signalCompose(next);
        const match = MENTION_QUERY.exec(next);
        if (match && mentions.length < MAX_MENTIONS) {
            const list = matchMembers(match[2]).filter((memberKey) => !mentions.includes(memberKey));
            if (list.length) {
                if (!picker) {
                    track('mention_open', {});
                }
                setPicker({ query: match[2], list, active: 0 });
                return;
            }
        }
        setPicker(null);
    }

    function pickMention(memberKey) {
        const at = picker?.replaceAt;
        setMentions((current) => {
            if (Number.isInteger(at)) {
                // 꼬리표를 눌러 다시 고른 것: 그 자리에 넣음
                return current.map((key, index) => (index === at ? memberKey : key));
            }
            return [...current, memberKey].slice(0, MAX_MENTIONS);
        });
        setValue((current) => current.replace(MENTION_QUERY, '$1'));
        setPicker(null);
        track('mention_pick', { member: memberKey });
        textareaRef.current?.focus();
    }

    /** 꼬리표를 누르면 그 자리 멤버를 다시 고르는 목록(다른 꼬리표 멤버는 뺌) */
    function reopenMention(index) {
        const others = mentions.filter((_, position) => position !== index);
        const list = MEMBER_KEYS.filter((memberKey) => !others.includes(memberKey));
        track('mention_open', { reselect: true });
        setPicker({ query: '', list, active: Math.max(0, list.indexOf(mentions[index])), replaceAt: index });
    }

    function submit() {
        if (!canSend) {
            return;
        }
        const text = [mentionPrefix(mentions), value.trim()].filter(Boolean).join(' ');
        track('message_send', { source: 'text', mentions: mentions.length });
        setValue('');
        setMentions([]);
        setPicker(null);
        // 보내면 서버가 입력 중을 끝냅니다(따로 'end' 를 보내지 않음)
        composeSentAt.current = 0;
        onSend(text);
    }

    function tapChip(item) {
        const text = [mentionPrefix(mentions), item.text].filter(Boolean).join(' ');
        track('message_send', { source: 'chip', mentions: mentions.length });
        setMentions([]);
        onChipTap(item, text);
    }

    function handleKeyDown(event) {
        if (picker) {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const step = event.key === 'ArrowDown' ? 1 : -1;
                setPicker((current) => ({
                    ...current,
                    active: (current.active + step + current.list.length) % current.list.length,
                }));
                return;
            }
            if (event.key === 'Enter') {
                event.preventDefault();
                pickMention(picker.list[picker.active]);
                return;
            }
            if (event.key === 'Escape') {
                setPicker(null);
                return;
            }
        }
        // 빈 입력칸에서 지우기 한 번 = 마지막 꼬리표 통째로
        if (event.key === 'Backspace' && !value && mentions.length) {
            event.preventDefault();
            setMentions((current) => current.slice(0, -1));
            return;
        }
        // Enter 는 줄바꿈(textarea 기본 동작). 보내기는 전송 버튼으로만
    }

    return (
        <div className="flex shrink-0 flex-col">
            {picker ? (
                <ul
                    role="listbox"
                    aria-label="멤버 부르기"
                    className="m-0 mx-4 mb-2 flex list-none flex-col rounded-2xl border border-[rgba(38,35,31,.14)] bg-surface p-1.5"
                >
                    {picker.list.map((memberKey, index) => (
                        <li key={memberKey} role="option" aria-selected={index === picker.active}>
                            <button
                                type="button"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => pickMention(memberKey)}
                                className={`flex h-12 w-full items-center gap-2.5 rounded-xl px-2 text-left ${
                                    index === picker.active ? 'bg-surface-sunken' : ''
                                }`}
                            >
                                <Avatar memberKey={memberKey} size={32} />
                                <span className="flex min-w-0 flex-col">
                                    <span className="text-[15px] leading-5 font-semibold">{nameOf(memberKey)}</span>
                                    <span className="truncate text-[12px] leading-4 text-text-secondary">
                                        {chips?.anchorMemberKey === memberKey && chips?.speechAct === 'question'
                                            ? '지금 물은 사람'
                                            : getMember(memberKey)?.short}
                                    </span>
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
            {showChips ? <ChipRow chips={chips} onTap={tapChip} /> : null}
            <form
                className="relative flex shrink-0 items-end border-t border-hairline bg-bg px-4 pt-2 pb-[max(10px,env(safe-area-inset-bottom))]"
                onSubmit={(event) => {
                    event.preventDefault();
                    submit();
                }}
            >
                <InputBox className="relative flex min-h-12 min-w-0 flex-1 flex-col items-stretch gap-1 rounded-[24px] border border-hairline bg-surface pl-2 pr-12 shadow-[0_2px_12px_rgba(38,35,31,0.05)] transition-colors focus-within:border-brand">
                    {mentions.length ? (
                        <div className="flex flex-wrap gap-1.5 pt-2 pl-2">
                            {mentions.map((memberKey, index) => (
                                <button
                                    key={memberKey}
                                    type="button"
                                    aria-label={`${nameOf(memberKey)}에게 보내기, 바꾸려면 두 번 탭`}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => reopenMention(index)}
                                    disabled={disabled}
                                    className="inline-flex h-[26px] items-center rounded-[13px] bg-surface-sunken px-2.5 text-[14px] font-semibold text-brand"
                                >
                                    @{nameOf(memberKey)}
                                </button>
                            ))}
                        </div>
                    ) : null}
                    <textarea
                        ref={textareaRef}
                        rows={1}
                        value={value}
                        maxLength={MAX_LENGTH}
                        disabled={disabled}
                        aria-label="별채에 한마디"
                        placeholder={mentions.length ? '' : '별채에 한마디'}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        className="block min-h-11 w-full min-w-0 resize-none border-0 bg-transparent py-2.5 pr-1 pl-2 text-body text-text-primary outline-none placeholder:text-text-tertiary"
                    />
                    <button
                        type="submit"
                        aria-label="보내기"
                        disabled={!canSend}
                        className="absolute right-1.5 bottom-1.5 flex size-9 items-center justify-center rounded-full bg-brand text-on-brand transition-[background-color,transform] duration-150 enabled:hover:brightness-110 enabled:active:scale-95 disabled:bg-[#EAE2D7] disabled:text-[#9C9082]"
                    >
                        <ArrowUp size={18} strokeWidth={2} aria-hidden="true" />
                    </button>
                </InputBox>
            </form>
        </div>
    );
}
